<template>
  <nav class="navbar" :class="{ scrolled: isScrolled }">
    <div class="navbar-inner">
      <!-- Logo -->
      <router-link class="navbar-logo" to="/">
        <span class="logo-jp">JLPT耳勉</span>
        <span class="logo-sub">JLPT Listening</span>
      </router-link>

      <!-- 桌面端导航 -->
      <div class="navbar-links hide-mobile">
        <template v-if="isLoggedIn && hasSubscription">
          <router-link class="nav-link" to="/study">学习</router-link>
          <router-link class="nav-link" to="/mondai4">问题四专练</router-link>
          <router-link class="nav-link" to="/vocabulary">生词本</router-link>
          <router-link class="nav-link nav-link-renew" to="/pricing">续费/升级</router-link>
        </template>
        <template v-else-if="isLoggedIn">
          <router-link class="nav-link" to="/mondai4">问题四专练</router-link>
          <router-link class="nav-link" to="/pricing">订阅方案</router-link>
        </template>
        <template v-else>
          <router-link class="nav-link" to="/pricing">订阅方案</router-link>
        </template>
      </div>

      <!-- 右侧操作区 -->
      <div class="navbar-actions">
        <!-- 日/夜模式切换 -->
        <button class="theme-toggle" @click="toggleTheme" :title="isDark ? '切换到白天模式' : '切换到夜间模式'">
          <transition name="theme-icon" mode="out-in">
            <svg v-if="isDark" key="sun" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="5"/>
              <line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/>
              <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
              <line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/>
              <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
            </svg>
            <svg v-else key="moon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
            </svg>
          </transition>
        </button>

        <!-- 未登录 -->
        <template v-if="!isLoggedIn">
          <router-link class="btn btn-ghost btn-sm" to="/login">登录</router-link>
          <router-link class="btn btn-primary btn-sm" to="/register">注册账号</router-link>
        </template>

        <!-- 已登录 -->
        <template v-else>
          <div class="user-menu" @click="toggleMenu" ref="userMenu">
            <div class="user-avatar">
              <img v-if="user.avatar" :src="user.avatar" :alt="user.username" />
              <span v-else>{{ user.username[0].toUpperCase() }}</span>
            </div>
            <span class="user-name hide-mobile">{{ user.username }}</span>
            <svg class="chevron" :class="{ open: menuOpen }" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>

            <!-- 下拉菜单 -->
            <transition name="dropdown">
              <div v-if="menuOpen" class="dropdown-menu">
                <div class="dropdown-header">
                  <div class="d-username">{{ user.username }}</div>
                  <div class="d-email">{{ user.email }}</div>
                  <span v-if="hasSubscription" class="badge badge-gold">{{ user.planName || '会员' }}</span>
                  <span v-else class="badge badge-sakura">未订阅</span>
                </div>
                <div class="dropdown-divider"></div>
                <template v-if="hasSubscription">
                  <router-link class="dropdown-item" to="/study">📖 开始学习</router-link>
                  <router-link class="dropdown-item" to="/mondai4">⚡ 问题四专练</router-link>
                  <router-link class="dropdown-item" to="/vocabulary">⭐ 我的生词本</router-link>
                  <router-link class="dropdown-item" to="/wrong-book">📔 我的错题本</router-link>
                </template>
                <template v-else>
                  <router-link class="dropdown-item" to="/mondai4">⚡ 问题四专练</router-link>
                  <router-link class="dropdown-item highlight" to="/pricing">🔓 立即订阅</router-link>
                </template>
                <div class="dropdown-divider"></div>
                <button class="dropdown-item danger" @click="handleLogout">退出登录</button>
              </div>
            </transition>
          </div>
        </template>

        <!-- 移动端汉堡菜单 -->
        <button class="hamburger hide-desktop" @click="mobileMenuOpen = !mobileMenuOpen">
          <span :class="{ active: mobileMenuOpen }"></span>
        </button>
      </div>
    </div>

    <!-- 移动端菜单 -->
    <transition name="mobile-menu">
      <div v-if="mobileMenuOpen" class="mobile-menu">
        <template v-if="isLoggedIn && hasSubscription">
          <router-link class="mobile-link" to="/study" @click.native="mobileMenuOpen = false">学习</router-link>
          <router-link class="mobile-link" to="/mondai4" @click.native="mobileMenuOpen = false">问题四专练</router-link>
          <router-link class="mobile-link" to="/vocabulary" @click.native="mobileMenuOpen = false">生词本</router-link>
          <router-link class="mobile-link" to="/wrong-book" @click.native="mobileMenuOpen = false">错题本</router-link>
        </template>
        <router-link
          v-else-if="isLoggedIn"
          class="mobile-link"
          to="/mondai4"
          @click.native="mobileMenuOpen = false"
        >问题四专练</router-link>
        <router-link class="mobile-link" to="/pricing" @click.native="mobileMenuOpen = false">订阅方案</router-link>
        <template v-if="!isLoggedIn">
          <router-link class="mobile-link" to="/login" @click.native="mobileMenuOpen = false">登录</router-link>
          <router-link class="mobile-link accent" to="/register" @click.native="mobileMenuOpen = false">注册账号</router-link>
        </template>
        <template v-else>
          <button class="mobile-link danger" @click="handleLogout">退出登录</button>
        </template>
      </div>
    </transition>
  </nav>
</template>

<script>
import { mapGetters } from 'vuex'

export default {
  name: 'AppNavbar',
  data () {
    return {
      isScrolled: false,
      menuOpen: false,
      mobileMenuOpen: false,
      isDark: false
    }
  },
  created () {
    this.isDark = (localStorage.getItem('jlpt_theme') || 'light') === 'dark'
  },
  computed: {
    ...mapGetters('auth', ['isLoggedIn', 'user', 'hasActiveSubscription']),
    hasSubscription () { return this.hasActiveSubscription }
  },
  mounted () {
    window.addEventListener('scroll', this.onScroll)
    document.addEventListener('click', this.onDocClick)
  },
  beforeDestroy () {
    window.removeEventListener('scroll', this.onScroll)
    document.removeEventListener('click', this.onDocClick)
  },
  methods: {
    onScroll () { this.isScrolled = window.scrollY > 20 },
    toggleMenu () { this.menuOpen = !this.menuOpen },
    onDocClick (e) {
      if (this.$refs.userMenu && !this.$refs.userMenu.contains(e.target)) {
        this.menuOpen = false
      }
    },
    handleLogout () {
      this.$store.dispatch('auth/logout')
      this.menuOpen = false
      this.mobileMenuOpen = false
      this.$router.push({ name: 'Home' })
    },
    toggleTheme () {
      this.isDark = !this.isDark
      const theme = this.isDark ? 'dark' : 'light'
      document.documentElement.setAttribute('data-theme', theme)
      localStorage.setItem('jlpt_theme', theme)
    }
  }
}
</script>

<style scoped>
.navbar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  height: var(--navbar-height);
  transition: all var(--transition-base);
  background: transparent;
}
.navbar.scrolled {
  background: var(--bg-overlay);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-bottom: 1px solid var(--border-color);
  box-shadow: var(--shadow-md);
}

.navbar-inner {
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 24px;
  height: 100%;
  display: flex;
  align-items: center;
  gap: 32px;
}

.navbar-logo {
  display: flex;
  flex-direction: column;
  line-height: 1;
  text-decoration: none;
  flex-shrink: 0;
}
.logo-jp {
  font-family: var(--font-serif);
  font-size: 1.25rem;
  font-weight: 700;
  background: linear-gradient(to right, #d04060, var(--accent-sakura), var(--accent-gold));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}
.logo-sub {
  font-size: 0.65rem;
  color: var(--text-muted);
  letter-spacing: 0.05em;
  margin-top: 10px;
}

.navbar-links {
  display: flex;
  align-items: center;
  gap: 4px;
  flex: 1;
}
.nav-link {
  padding: 6px 16px;
  border-radius: var(--radius-full);
  color: var(--text-secondary);
  font-size: 0.9rem;
  transition: all var(--transition-fast);
  text-decoration: none;
}
.nav-link:hover, .nav-link.router-link-active {
  color: var(--text-primary);
  background: var(--bg-card-hover);
}
.nav-link-renew {
  font-size: 0.8rem;
  opacity: 0.65;
}
.nav-link-renew:hover { opacity: 1; }

/* 主题切换按钮 */
.theme-toggle {
  width: 36px;
  height: 36px;
  border-radius: var(--radius-full);
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  color: var(--text-secondary);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all var(--transition-fast);
  flex-shrink: 0;
}
.theme-toggle:hover {
  color: var(--accent-gold);
  border-color: var(--accent-gold);
  background: var(--accent-gold-dim);
  transform: rotate(15deg);
}

.theme-icon-enter-active,
.theme-icon-leave-active { transition: all 0.2s ease; }
.theme-icon-enter { opacity: 0; transform: rotate(-90deg) scale(0.6); }
.theme-icon-leave-to { opacity: 0; transform: rotate(90deg) scale(0.6); }

.navbar-actions {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 12px;
}

/* 用户菜单 */
.user-menu {
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  border-radius: var(--radius-full);
  cursor: pointer;
  border: 1px solid var(--border-color);
  transition: all var(--transition-fast);
}
.user-menu:hover { border-color: var(--border-color-hover); background: var(--bg-card); }

.user-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--accent-sakura), var(--accent-gold));
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.85rem;
  overflow: hidden;
  flex-shrink: 0;
}
.user-avatar img { width: 100%; height: 100%; object-fit: cover; }
.user-name { font-size: 0.875rem; color: var(--text-primary); }
.chevron { color: var(--text-muted); transition: transform var(--transition-fast); }
.chevron.open { transform: rotate(180deg); }

/* 下拉菜单 */
.dropdown-menu {
  position: absolute;
  top: calc(100% + 12px);
  right: 0;
  min-width: 220px;
  background: var(--bg-tertiary);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-lg);
  padding: 8px;
  box-shadow: var(--shadow-lg);
  z-index: 100;
}
.dropdown-header { padding: 8px 12px 12px; }
.d-username { font-weight: 600; font-size: 0.9rem; margin-bottom: 2px; }
.d-email { font-size: 0.78rem; color: var(--text-muted); margin-bottom: 8px; }
.dropdown-divider { height: 1px; background: var(--border-color); margin: 6px 0; }
.dropdown-item {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 9px 12px;
  border-radius: var(--radius-sm);
  font-size: 0.875rem;
  color: var(--text-secondary);
  text-decoration: none;
  background: none;
  border: none;
  cursor: pointer;
  transition: all var(--transition-fast);
  text-align: left;
}
.dropdown-item:hover { background: var(--bg-card-hover); color: var(--text-primary); }
.dropdown-item.highlight { color: var(--accent-sakura); }
.dropdown-item.highlight:hover { background: var(--accent-sakura-dim); }
.dropdown-item.danger { color: #ff6b6b; }
.dropdown-item.danger:hover { background: rgba(255,107,107,0.1); }

/* 动画 */
.dropdown-enter-active, .dropdown-leave-active {
  transition: all 0.2s ease;
  transform-origin: top right;
}
.dropdown-enter, .dropdown-leave-to {
  opacity: 0;
  transform: scale(0.95) translateY(-8px);
}

/* 移动端汉堡 */
.hamburger {
  width: 36px;
  height: 36px;
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-sm);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: 0;
}
.hamburger span,
.hamburger span::before,
.hamburger span::after {
  display: block;
  width: 18px;
  height: 2px;
  background: var(--text-primary);
  border-radius: 2px;
  transition: all var(--transition-base);
  position: relative;
}
.hamburger span::before,
.hamburger span::after {
  content: '';
  position: absolute;
}
.hamburger span::before { top: -5px; }
.hamburger span::after { top: 5px; }
.hamburger span.active { background: transparent; }
.hamburger span.active::before { transform: rotate(45deg); top: 0; }
.hamburger span.active::after { transform: rotate(-45deg); top: 0; }

/* 移动端菜单 */
.mobile-menu {
  background: rgba(12, 20, 40, 0.98);
  backdrop-filter: blur(20px);
  border-top: 1px solid var(--border-color);
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.mobile-link {
  display: block;
  padding: 12px 16px;
  border-radius: var(--radius-md);
  color: rgba(255,255,255,0.75);
  font-size: 0.95rem;
  text-decoration: none;
  transition: all var(--transition-fast);
  background: none;
  border: none;
  text-align: left;
  cursor: pointer;
  width: 100%;
}
.mobile-link:hover, .mobile-link.router-link-active { color: #fff; background: rgba(255,255,255,0.08); }
.mobile-link.accent { color: var(--accent-sakura); }
.mobile-link.danger { color: #ff6b6b; }

.mobile-menu-enter-active, .mobile-menu-leave-active { transition: all 0.25s ease; }
.mobile-menu-enter, .mobile-menu-leave-to { opacity: 0; transform: translateY(-10px); }
</style>
